/**
 * Query pipeline — parse / compile `filter |> aggregate |> render` (PURE).
 *
 * The Explore query language (queryLang.js) is filter-only: it compiles to one
 * FilterExpr that refines the scope. This module extends it into a 3-stage
 * pipeline so a plain-text query becomes a lightweight, savable chart — a
 * "kernel-lite" — without writing Python or opening the chat:
 *
 *     <filter> |> <aggregate> |> <render>
 *
 *   filter    — the existing query language, unchanged → spec.filter.
 *   aggregate — count | uniq <f> | sum/avg/min/max/p50/p95/p99 <f>;
 *               by <dim> or by <time>; optional `top N`
 *               → spec.output.{dimensions,metrics} + orderBy + limit.
 *   render    — bar | line | table | number | donut | …  → the render spec.
 *               Optional: inferred from the aggregate shape when omitted.
 *
 * The separator is `|>` (not `|`) because `|` already means OR inside the
 * filter grammar. A pipeline compiles to a STANDARD one-step kernel
 * ({ steps: [{ kind: "analytics_query", spec }], render, renderSource }) that
 * the existing kernel runner + KernelBlock execute and render — saving it is a
 * normal user kernel, and "graduating" to Python is forking it and adding a
 * python step. No new runtime.
 *
 * PURE: tokenizer + the static aggregate/render vocab + the spec builder. The
 * only imports are the pure query-language modules, so this unit-tests in
 * isolation (no React/network) and reuses the same field catalog.
 *
 * v1 scope notes (deliberate cuts, surfaced as warnings — not silent):
 *   - Aggregates/groups target PROMPT-direct fields (alias `p`). Grouping by a
 *     join-only dim (cluster/identity) is deferred; the FILTER stage still
 *     supports them. `endpoint` aggregates on the prompt-direct endpoint id.
 *   - Token aggregates need the ENTITY_TYPE_TOKEN root (a different, always-
 *     global root) — deferred to a later phase.
 */

import { parseQuery, serializeQuery, resolveField } from "./queryLang.js";
import { compileQuery } from "./queryLangCompile.js";
import { ensureQueryJoins } from "./queryLangJoins.js";

// ── vocab ────────────────────────────────────────────────────────────────

// Aggregate verbs → proto AggregateOp. `count` is arity-0 (counts prompts via
// the event id); the rest take one field. uniq/distinct are the same op.
const AGG_OPS = {
  count: { op: "AGGREGATE_OP_COUNT", arity: 0 },
  uniq: { op: "AGGREGATE_OP_COUNT_DISTINCT", arity: 1 },
  distinct: { op: "AGGREGATE_OP_COUNT_DISTINCT", arity: 1 },
  sum: { op: "AGGREGATE_OP_SUM", arity: 1, numeric: true },
  avg: { op: "AGGREGATE_OP_AVG", arity: 1, numeric: true },
  min: { op: "AGGREGATE_OP_MIN", arity: 1, numeric: true },
  max: { op: "AGGREGATE_OP_MAX", arity: 1, numeric: true },
  p50: { op: "AGGREGATE_OP_P50", arity: 1, numeric: true },
  p95: { op: "AGGREGATE_OP_P95", arity: 1, numeric: true },
  p99: { op: "AGGREGATE_OP_P99", arity: 1, numeric: true },
};

export const AGG_VERBS = Object.keys(AGG_OPS);

// `by <time>` keywords → bucket width in seconds. Whole words only — `min`
// would collide with the min aggregate verb, so minute-granularity is `minute`.
export const TIME_BUCKETS = {
  minute: 60,
  hour: 3600,
  day: 86400,
  week: 604800,
  month: 2592000,
};

// Friendly render word → kernel render primitive kind. Several words map to one
// primitive (number/stat/kpi → kpis; line/area/trend → chart; pie/ratio → donut).
export const RENDER_KEYWORDS = {
  bar: "bars", bars: "bars",
  line: "chart", area: "chart", trend: "chart",
  table: "table",
  number: "kpis", stat: "kpis", kpi: "kpis", kpis: "kpis",
  donut: "donut", ratio: "donut", pie: "donut",
  treemap: "treemap",
  heatmap: "heatmap",
  scatter: "scatter",
  rows: "rows", list: "rows",
  sparkline: "sparkline", spark: "sparkline",
  funnel: "funnel",
  calendar: "calendar",
};

// Aggregating/grouping a join-only or text/synthetic field isn't expressible as
// a PROMPT-direct dimension in v1. `endpoint` is special-cased to the prompt-
// direct endpoint id (the join maps to a hostname, which isn't on the prompt row).
const AGG_FIELD_OVERRIDE = {
  endpoint: { step: "p", field: "DIMENSION_FIELD_PROMPT_ENDPOINT_ID" },
};

const days = (n) => ({ relative: { durationSeconds: String(n * 86400) } });

// ── stage split ────────────────────────────────────────────────────────────

/**
 * Split an input on the `|>` stage separator, quote-aware (a `|>` inside a
 * double-quoted filter value does not split). Returns trimmed stage strings;
 * a non-pipeline input returns a single-element array.
 */
export function splitPipeline(input) {
  const out = [];
  let buf = "";
  let inQuote = false;
  const s = input || "";
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === '"') { inQuote = !inQuote; buf += c; continue; }
    if (!inQuote && c === "|" && s[i + 1] === ">") { out.push(buf); buf = ""; i++; continue; }
    buf += c;
  }
  out.push(buf);
  return out.map((p) => p.trim());
}

/** True when the input carries a `|>` stage separator (syntactic only). */
export function isPipeline(input) {
  return splitPipeline(input).length > 1;
}

/**
 * True when the input is a REAL pipeline — a `|>` whose aggregate stage starts
 * with a recognized measure. This (not the bare `isPipeline`) gates pipeline
 * activation, so a durable saved search that merely *contains* an unquoted `|>`
 * (e.g. Elixir/F#/shell pipe code typed as free text) stays a plain full-text
 * filter instead of silently flipping into a broken chart when it's reloaded.
 */
export function looksLikePipeline(input) {
  if (!isPipeline(input)) return false;
  // The aggregate stage must START (token 0) with a recognized measure — anchored
  // to the start, not merely "contains a measure" — so a saved free-text search
  // that happens to include `|>` (`foo |> bar baz`) stays a plain filter. The only
  // irreducible reinterpretation is text whose post-`|>` segment genuinely begins
  // with an aggregate verb (`data |> count`); the trace list/metrics still compile
  // only the filter stage, and deleting the `|>` recovers it.
  const firstTok = (splitPipeline(input)[1] || "").trim().split(/[\s,]+/)[0]?.toLowerCase();
  return !!(firstTok && AGG_OPS[firstTok]);
}

/**
 * The filter-stage text of an input — the part before the first `|>` (or the
 * whole input when it's not a real pipeline). Surfaces that compile the box as a
 * plain filter (the trace list, overview metrics) pass this so they never try
 * to compile the aggregate/render words as filter terms.
 */
export function pipelineFilterText(input) {
  return looksLikePipeline(input) ? splitPipeline(input)[0] : (input || "");
}

/**
 * AND two filter-query strings into one (e.g. layer the scope steer onto the
 * trace-window query). Composes through the parser/serializer rather than
 * string concatenation so OR-precedence and quoting survive — `a OR b` + `c`
 * becomes `(a OR b) c`, not `a OR b c`. Empty/blank operands drop out.
 */
export function andQueries(a, b) {
  const qa = pipelineFilterText(a).trim();
  const qb = pipelineFilterText(b).trim();
  if (!qa) return qb;
  if (!qb) return qa;
  const astA = parseQuery(qa).ast;
  const astB = parseQuery(qb).ast;
  if (!astA) return qb;
  if (!astB) return qa;
  return serializeQuery({ type: "and", children: [astA, astB] });
}

/**
 * AND a list of filter strings into one (the accumulated scope steer: every
 * level's steer down the drill path, ANDed). Empties drop out; order preserved.
 */
export function andQueriesAll(parts) {
  return (parts || []).reduce((acc, p) => andQueries(acc, p), "");
}

// ── aggregate stage ──────────────────────────────────────────────────────

/**
 * Parse the aggregate stage text into { metrics, groups, topN, errors, warnings }.
 * Grammar: measure+ ("by" group)* ("top"|"limit" N)?  — commas optional.
 * Field/group resolution is deferred to compile (this is purely syntactic).
 */
export function parseAggregate(text) {
  const errors = [];
  const warnings = [];
  const metrics = [];
  const groups = [];
  let topN = null;
  const toks = (text || "").split(/[\s,]+/).filter(Boolean);
  let i = 0;

  // leading measures, until a `by`/`top`/`limit` keyword or end
  while (i < toks.length) {
    const t = toks[i].toLowerCase();
    if (t === "by" || t === "top" || t === "limit") break;
    const spec = AGG_OPS[t];
    if (!spec) {
      errors.push({ message: `unknown aggregate "${toks[i]}" — try count, uniq <field>, sum <field>` });
      i++;
      continue;
    }
    if (spec.arity === 0) {
      metrics.push({ verb: t });
      i++;
    } else {
      const f = toks[i + 1];
      // A following keyword (`by`/`top`/`limit`) or another verb isn't the field
      // — `sum top 5` should say "needs a field", not swallow `top` as the field.
      if (!f || AGG_OPS[f.toLowerCase()] || ["by", "top", "limit"].includes(f.toLowerCase())) {
        errors.push({ message: `${t} needs a field, e.g. ${t} tool_count` });
        i++;
      } else {
        metrics.push({ verb: t, fieldKey: f.toLowerCase() });
        i += 2;
      }
    }
  }

  // by-groups and top/limit, in any order
  while (i < toks.length) {
    const t = toks[i].toLowerCase();
    if (t === "by") {
      const g = toks[i + 1];
      if (!g) { errors.push({ message: "`by` needs a field or time (day, hour, week…)" }); i++; break; }
      groups.push({ raw: g.toLowerCase() });
      i += 2;
      continue;
    }
    if (t === "top" || t === "limit") {
      const n = Number(toks[i + 1]);
      if (Number.isFinite(n) && n > 0) { topN = Math.floor(n); i += 2; }
      else { errors.push({ message: "`top` needs a number, e.g. top 10" }); i++; }
      continue;
    }
    warnings.push({ message: `ignored "${toks.slice(i).join(" ")}"` });
    break;
  }

  return { metrics, groups, topN, errors, warnings };
}

// Resolve an aggregate/group field key to a PROMPT-direct FieldRef, or null with
// a reason. `numeric` requires an int-kind field (sum/avg/percentiles).
function resolveAggField(key, { numeric = false } = {}) {
  if (AGG_FIELD_OVERRIDE[key]) return { ref: AGG_FIELD_OVERRIDE[key], label: key };
  const r = resolveField(key);
  if (!r) return { error: `unknown field "${key}"` };
  const def = r.def;
  if (def.step !== "p" || !def.field) return { error: `can't aggregate by "${key}" yet (needs a join)` };
  if (def.kind === "synthetic") return { error: `can't aggregate by "${key}" (it's a membership flag)` };
  if (def.kind === "text") return { error: `won't aggregate by "${key}" (full prompt text)` };
  if (numeric && def.kind !== "int") return { error: `"${key}" isn't numeric — sum/avg/min/max/percentiles need a number field` };
  return { ref: { step: def.step, field: def.field }, label: r.key };
}

// ── render stage ───────────────────────────────────────────────────────────

/** Parse the render stage into { kind, raw, errors }. Empty → { kind: null }. */
export function parseRender(text) {
  const errors = [];
  const raw = (text || "").trim().toLowerCase().split(/\s+/)[0] || "";
  if (!raw) return { kind: null, raw: "", errors };
  const kind = RENDER_KEYWORDS[raw];
  if (!kind) errors.push({ message: `unknown chart "${raw}" — try bar, line, table, number, donut…` });
  return { kind: kind || null, raw, errors };
}

// ── compile ────────────────────────────────────────────────────────────────

const TS_FIELD = "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP";
const EVENT_ID = "DIMENSION_FIELD_PROMPT_EVENT_ID";

// Human label + output alias for a resolved metric.
function metricLabel(m) {
  if (m.verb === "count") return "Prompts";
  const verbLabel = { uniq: "Distinct", distinct: "Distinct", sum: "Sum", avg: "Avg", min: "Min", max: "Max", p50: "p50", p95: "p95", p99: "p99" }[m.verb];
  return `${verbLabel} ${m.fieldKey}`;
}
function metricAliasName(m, single) {
  if (single) return "value";
  if (m.verb === "count") return "count";
  return `${m.verb}_${m.fieldKey}`;
}

// Map a time bucket to the chart hover-range label (cosmetic only). 1Q (90d)
// is the longest span now, so coarse buckets cap there.
const BUCKET_RANGE = { minute: "1D", hour: "5D", day: "1M", week: "1Q", month: "1Q" };

function inferKind(groups, metrics) {
  if (groups.length === 0) return "kpis";
  const hasTime = groups.some((g) => g.isTime);
  if (groups.length === 1 && metrics.length === 1) return hasTime ? "chart" : "bars";
  if (groups.length === 2 && metrics.length === 1) return "heatmap";
  return "table";
}

// Build a render spec for `kind` from the resolved groups + metrics. Falls back
// to `table` (with a warning) when the requested kind needs a shape the
// aggregate didn't produce.
function buildRender(kind, groups, metrics, topN, warnings) {
  const m0 = metrics[0];
  const g0 = groups[0];
  const g1 = groups[1];
  const timeRange = BUCKET_RANGE[groups.find((g) => g.isTime)?.timeWord] || "1M";
  const fallback = (msg) => {
    warnings.push({ message: msg });
    return tableRender(groups, metrics, topN);
  };
  switch (kind) {
    case "kpis":
      if (groups.length) return fallback("a single number ignores grouping — showing a table");
      return { kind: "kpis", tiles: metrics.map((m) => ({ label: m.label, valueField: m.alias, format: "integer" })) };
    case "chart":
      if (metrics.length > 1) return fallback("line chart shows one metric — showing a table");
      // No `title` — the activity-surface panel header / saved-kernel tab already
      // names it; a chart caption would double-label (and lens charts have none).
      return { kind: "chart", mode: "line", valueField: m0.alias, range: timeRange };
    case "bars":
      if (!g0 || metrics.length > 1) return fallback("bars need one group + one metric — showing a table");
      return { kind: "bars", labelField: g0.alias, valueField: m0.alias, format: "integer", maxItems: topN || 15 };
    case "treemap":
      if (!g0) return fallback("treemap needs a group — showing a table");
      return { kind: "treemap", labelField: g0.alias, valueField: m0.alias, format: "integer", maxItems: topN || 15 };
    case "rows":
      if (!g0) return fallback("rows need a group — showing a table");
      return { kind: "rows", primaryField: g0.alias, rightField: m0.alias, rightFormat: "integer", maxRows: topN || 20 };
    case "donut":
      if (groups.length) return fallback("a donut shows a single value — showing a table");
      return { kind: "donut", valueField: m0.alias };
    case "sparkline":
      return { kind: "sparkline", series: m0.alias };
    case "funnel":
      if (!g0) return fallback("funnel needs a group — showing a table");
      return { kind: "funnel", labelField: g0.alias, valueField: m0.alias, format: "integer" };
    case "calendar":
      if (!groups.some((g) => g.isTime)) return fallback("calendar needs a time grouping (by day) — showing a table");
      return { kind: "calendar", dateField: "bucket", valueField: m0.alias };
    case "heatmap":
      if (!g0 || !g1) return fallback("heatmap needs two groups — showing a table");
      return { kind: "heatmap", xField: g0.alias, yField: g1.alias, valueField: m0.alias };
    case "scatter":
      if (metrics.length < 2) return fallback("scatter needs two metrics — showing a table");
      return { kind: "scatter", xField: metrics[0].alias, yField: metrics[1].alias, seriesField: g0?.alias };
    case "table":
    default:
      return tableRender(groups, metrics, topN);
  }
}

function tableRender(groups, metrics, topN) {
  return {
    kind: "table",
    columns: [
      ...groups.map((g) => ({ id: g.alias, label: g.label })),
      ...metrics.map((m) => ({ id: m.alias, label: m.label, format: "integer", align: "right" })),
    ],
    maxRows: topN || 50,
  };
}

function slugify(s) {
  return (s || "kernel").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48) || "kernel";
}

/**
 * Compile a pipeline input into a kernel draft. Returns:
 *   { isPipeline, kernel, errors, warnings, parsed }
 * `kernel` is null when the input isn't a pipeline (caller uses the plain
 * filter path) or when the aggregate stage is missing/uncompilable. `parsed`
 * exposes the resolved metrics/groups/render for the bar UI.
 *
 * opts: { timeRange?, adapterResolver?, scopeMode? } — timeRange defaults to
 * 30 days; adapterResolver is forwarded to compileQuery for synthetic filter dims.
 */
export function compilePipeline(input, opts = {}) {
  if (!looksLikePipeline(input)) return { isPipeline: false, kernel: null, errors: [], warnings: [], parsed: null };

  const errors = [];
  const warnings = [];
  const stages = splitPipeline(input);
  if (stages.length > 3) errors.push({ message: "too many stages — expected filter |> aggregate |> render" });

  const [filterText, aggText = "", renderText = ""] = stages;

  // Stage 1: filter (reuse the existing language verbatim).
  const { ast } = parseQuery(filterText);
  const compiledFilter = compileQuery(ast, { adapterResolver: opts.adapterResolver });
  errors.push(...compiledFilter.errors);
  warnings.push(...compiledFilter.warnings);

  // Stage 2: aggregate.
  const agg = parseAggregate(aggText);
  errors.push(...agg.errors);
  warnings.push(...agg.warnings);
  if (agg.metrics.length === 0 && agg.errors.length === 0) {
    errors.push({ message: "add an aggregate, e.g. `count by day`" });
  }

  // Resolve metrics → metric specs (+ aliases).
  const single = agg.metrics.length === 1;
  const metrics = [];
  for (const m of agg.metrics) {
    const alias = metricAliasName(m, single);
    const label = metricLabel(m);
    if (m.verb === "count") {
      metrics.push({ alias, label, verb: m.verb, fieldKey: null, computed: { field: { stepAlias: "p", field: EVENT_ID }, op: AGG_OPS.count.op } });
      continue;
    }
    const numeric = !!AGG_OPS[m.verb].numeric;
    const rf = resolveAggField(m.fieldKey, { numeric });
    if (rf.error) { errors.push({ message: rf.error }); continue; }
    metrics.push({
      alias, label, verb: m.verb, fieldKey: m.fieldKey,
      computed: { field: { stepAlias: rf.ref.step, field: rf.ref.field }, op: AGG_OPS[m.verb].op },
    });
  }

  // Resolve groups → dimension specs (+ aliases). Time keywords bucket the
  // timestamp; otherwise a PROMPT-direct field.
  const groups = [];
  for (const g of agg.groups) {
    if (TIME_BUCKETS[g.raw]) {
      groups.push({
        alias: "bucket", label: g.raw, isTime: true, timeWord: g.raw,
        dim: { field: { stepAlias: "p", field: TS_FIELD }, outputAlias: "bucket", bucketSeconds: TIME_BUCKETS[g.raw], fillBuckets: true },
      });
      continue;
    }
    const rf = resolveAggField(g.raw);
    if (rf.error) { errors.push({ message: rf.error }); continue; }
    groups.push({
      alias: rf.label, label: rf.label, isTime: false,
      dim: { field: { stepAlias: rf.ref.step, field: rf.ref.field }, outputAlias: rf.label },
    });
  }

  // Stage 3: render (explicit keyword overrides the inferred kind).
  const renderParsed = parseRender(renderText);
  errors.push(...renderParsed.errors);

  if (errors.length > 0 || metrics.length === 0) {
    return { isPipeline: true, kernel: null, errors, warnings, parsed: { metrics, groups, render: renderParsed, topN: agg.topN } };
  }

  // Assemble the analytics_query spec.
  const output = { metrics: metrics.map((m) => ({ outputAlias: m.alias, computed: m.computed })) };
  if (groups.length) output.dimensions = groups.map((g) => g.dim);

  let root = { alias: "p", entity: "ENTITY_TYPE_PROMPT" };
  if (compiledFilter.joins.length) root = ensureQueryJoins(root, compiledFilter.joins);

  const spec = {
    root,
    timeRange: opts.timeRange || days(30),
    output,
  };
  if (compiledFilter.filter) spec.filter = compiledFilter.filter;

  const hasTime = groups.some((g) => g.isTime);
  if (hasTime) spec.orderBy = [{ outputAlias: "bucket", direction: "DIRECTION_ASC" }];
  else if (groups.length) spec.orderBy = [{ outputAlias: metrics[0].alias, direction: "DIRECTION_DESC" }];

  if (agg.topN) spec.limit = agg.topN;
  else if (groups.length && !hasTime) spec.limit = 15;

  let kind = renderParsed.kind || inferKind(groups, metrics);
  // A no-group result is a single value — only kpis/donut make sense. Anything
  // else (a line/bar with nothing to plot against) would degrade silently, so
  // override to the total with a warning.
  if (groups.length === 0 && kind !== "kpis" && kind !== "donut") {
    warnings.push({ message: `"${renderParsed.raw || kind}" needs a grouping (e.g. "by day") — showing the total` });
    kind = "kpis";
  }
  const render = buildRender(kind, groups, metrics, agg.topN, warnings);

  // donut and multi-tile kpis need ONE object, not the 1-row array the analytics
  // step yields — collapse it with a pick step (as the built-in single-value
  // kernels do), else they render blank. A single-metric kpis reads data[0] fine,
  // so the headline `count` stays a pure one-query kernel.
  const steps = [{ kind: "analytics_query", id: "q", spec }];
  let renderSource = "q";
  if (groups.length === 0 && (kind === "donut" || metrics.length > 1)) {
    steps.push({ kind: "python", id: "pick", inputs: { rows: "q" }, code: "result = (inputs.get('rows') or [{}])[0]\n" });
    renderSource = "pick";
  }

  const title = `${metrics.map((m) => m.label).join(" + ")}${groups.length ? " by " + groups.map((g) => g.label).join(" × ") : ""}`;
  const kernel = {
    // Stable identity for the ephemeral preview (KernelWidget keys its run on
    // it); a real id is stamped only if the user saves it as a kernel.
    id: `pipeline:${input}`,
    command: slugify(title),
    title,
    scopeMode: opts.scopeMode || "scoped",
    params: {},
    steps,
    renderSource,
    render,
  };

  return { isPipeline: true, kernel, errors, warnings, parsed: { metrics, groups, render: { ...renderParsed, kind }, topN: agg.topN } };
}
